import { useQuery } from '@apollo/react-hooks';
import { useState, useEffect, useMemo } from 'react';
import GetUsersAdmin from '../gql/queries/get-users-admin';

const useUserTable = (searchTerm = '', roleFilter = '') => {
  const [currentPage, setCurrentPage] = useState(1);
  const [appliedFilters, setAppliedFilters] = useState({
    role: '',
    inactive: null,
  });

  const itemsPerPage = 15;

  // Build input variables for the query
  const queryVariables = useMemo(() => {
    const filters = {};

    // Add filters (role and inactive go in filters object)
    if (roleFilter) filters.role = roleFilter;
    if (appliedFilters.role && !roleFilter) filters.role = appliedFilters.role;
    if (appliedFilters.inactive !== null)
      filters.inactive = appliedFilters.inactive;

    return {
      input: {
        // search is at top level, not in filters
        ...(searchTerm ? { search: searchTerm } : {}),
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        ...(Object.keys(filters).length > 0 ? { filters } : {}),
      },
    };
  }, [currentPage, searchTerm, roleFilter, appliedFilters]);

  // Execute the query
  const { data, loading, error, refetch } = useQuery(GetUsersAdmin, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
  });

  // Transform the data to match the component's expected format
  const users = useMemo(() => {
    if (!data?.getUsersAdmin?.results) return [];

    return data.getUsersAdmin.results.map((user) => ({
      id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      middle_name: user.middle_name,
      suffix: user.suffix,
      email: user.email,
      cellphone: user.cellphone,
      customer_since: user.customer_since,
      events_attended_total: user.events_attended_total,
      lifetime_value_total: user.lifetime_value_total,
      last_purchase_date: user.last_purchase_date,
      last_purchase_at: user.last_purchase_at,
      last_purchase_id: user.last_purchase_id,
    }));
  }, [data]);

  const totalCount = data?.getUsersAdmin?.count || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, appliedFilters]);

  return {
    users,
    loading,
    error,
    currentPage,
    setCurrentPage,
    totalPages,
    totalCount,
    itemsPerPage,
    appliedFilters,
    setAppliedFilters,
    refetch,
  };
};

export default useUserTable;
